import { useLazyQuery } from '@oneaudi/onegraph-client';
import { useAtom } from 'jotai';
import { useCallback } from 'react';
import { useAppDispatch, useAppState } from '~/app/contexts/app-context';
import { useMarketConfig } from '~/app/market-config';
import { ConfiguredCarFinancing, FinancingDocument, FinancingInput } from '~/graphql/__generated__/graphql';
import { useConfigurator } from '~/graphql/hooks/use-configurator';
import { state } from '~/storage';

export function useFinancing() {
	const { config } = useMarketConfig();
	const { carIdentifier } = useConfigurator();
	const dispatch = useAppDispatch();
	const { financingData } = useAppState();
	const [financialError, setFinancialError] = useAtom(state.financialError);
	const { fetchFinancing, isLoadingFinancing } = useFinancingLazyQuery();

	const dispatchFinancing = useCallback(
		(newFinancing: Partial<ConfiguredCarFinancing>, loading: boolean) => {
			dispatch({ type: 'SET_FINANCING_DATA', payload: { financing: newFinancing, loading } });
		},
		[dispatch],
	);

	const getFinancing = useCallback(async () => {
		if (config.enableFinancingServices) {
			dispatchFinancing(financingData?.financing || ({} as ConfiguredCarFinancing), true);
			try {
				const result = await fetchFinancing({
					variables: {
						configuredCarIdentifier: carIdentifier,
						financingInput: config.financingInput as FinancingInput,
						enablePartsRateCalculation: config.enablePartsRateCalculation,
					},
				});
				if (result.data?.configuredCar.financing) {
					dispatchFinancing(result.data?.configuredCar.financing ?? ({} as ConfiguredCarFinancing), false);
				} else {
					setFinancialError(true);
					dispatchFinancing({} as ConfiguredCarFinancing, false);
				}
			} catch (error) {
				setFinancialError(true);
				dispatchFinancing({} as ConfiguredCarFinancing, false);
			}
		}
	}, [
		JSON.stringify(carIdentifier),
		fetchFinancing,
		financingData?.financing,
		config.financingInput,
		dispatchFinancing,
		config.enableFinancingServices,
		setFinancialError,
		financialError,
	]);

	const partRateByPr = (prNumber: string | undefined): number | undefined => {
		if (prNumber) {
			const partRatesMap = financingData?.financing?.partRates?.reduce(
				(acc, item) => {
					if (item?.pr) acc[item.pr] = item.rate;
					return acc;
				},
				{} as Record<string, number>,
			);

			const matchingKey = Object.keys(partRatesMap || {}).find((key) => prNumber.endsWith(key));

			return matchingKey ? partRatesMap?.[matchingKey] : undefined;
		}
		return undefined;
	};

	return { getFinancing, isLoadingFinancing, partRateByPr };
}

function useFinancingLazyQuery() {
	const [fetchFinancing, { loading: isLoadingFinancing }] = useLazyQuery(FinancingDocument, {
		returnPartialData: true,
		fetchPolicy: 'no-cache',
	});
	return { fetchFinancing, isLoadingFinancing };
}
